import { z } from 'zod';

/**
 * Brand themes and accessibility settings (BB-60, CNT-012, IAM-011). A theme holds the colours, fonts and radii the
 * in-app and banner editors start from; one theme per environment is the default. The accessibility validator uses
 * the theme's colours where content leaves them unset; its rules warn unless the environment raises them to error,
 * which blocks publishing.
 */
const COLOR = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$|^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+|\d{1,3}%)\s*)?\)$/;
export const BrandColor = z.string().trim().regex(COLOR, 'a hex colour (#rgb, #rrggbb, #rrggbbaa) or rgb()/rgba()');

export const BrandTokens = z.strictObject({
  colors: z.strictObject({ primary: BrandColor, background: BrandColor, text: BrandColor, button: BrandColor, buttonText: BrandColor }),
  fonts: z.strictObject({ family: z.string().trim().min(1).max(100), titleSize: z.number().min(8).max(64), bodySize: z.number().min(8).max(64), buttonSize: z.number().min(8).max(64) }).default({ family: 'System', titleSize: 20, bodySize: 15, buttonSize: 16 }),
  radii: z.strictObject({ button: z.number().min(0).max(64), container: z.number().min(0).max(64) }).default({ button: 8, container: 12 }),
});
export type BrandTokens = z.infer<typeof BrandTokens>;

export const CreateBrandThemeBody = z.strictObject({ name: z.string().trim().min(1).max(80), tokens: BrandTokens, isDefault: z.boolean().default(false) });
export type CreateBrandThemeBody = z.infer<typeof CreateBrandThemeBody>;
export const UpdateBrandThemeBody = z.strictObject({ name: z.string().trim().min(1).max(80).optional(), tokens: BrandTokens.optional() });
export type UpdateBrandThemeBody = z.infer<typeof UpdateBrandThemeBody>;

export interface BrandThemeView {
  id: string;
  name: string;
  tokens: BrandTokens;
  isDefault: boolean;
  createdBy: { id: string; email: string | null } | null;
  updatedAt: string;
}

export const A11Y_CODES = ['missing_alt_text', 'low_contrast', 'missing_button_label', 'small_tap_target', 'missing_accessibility_language'] as const;
export type A11yCode = (typeof A11Y_CODES)[number];
export const A11ySettings = z.strictObject({
  severity: z.partialRecord(z.enum(A11Y_CODES), z.enum(['warning', 'error'])).default({}),
  /** Messages are localized, so each needs an accessibility language. */
  localizationEnabled: z.boolean().default(false),
});
export type A11ySettings = z.infer<typeof A11ySettings>;

export const ValidateA11yBody = z.strictObject({
  channel: z.enum(['content_card', 'in_app', 'banner']),
  content: z.record(z.string(), z.unknown()),
  /** The default theme when absent. */
  themeId: z.uuid().optional(),
});
export type ValidateA11yBody = z.infer<typeof ValidateA11yBody>;

export interface A11yIssueView {
  code: A11yCode;
  severity: 'warning' | 'error';
  path: string;
  message: string;
  details?: Record<string, unknown>;
}
export interface A11yCheck {
  issues: A11yIssueView[];
  /** An error-level issue blocks publishing. */
  blocking: boolean;
  themeId: string | null;
}
